import React, { useRef, useState } from "react";

// Google-Maps-style pull-up sheet (mobile): drag the handle bar or tap it
// to switch between a collapsed peek and the expanded card.
const COLLAPSE_DRAG_THRESHOLD_PX = 60;
const TAP_THRESHOLD_PX = 8;
// Height of the handle area that stays visible when collapsed.
const PEEK = "2.5rem";

const BottomSheet = ({
  expanded,
  setExpanded,
  children,
}: {
  expanded: boolean;
  setExpanded: (expanded: boolean) => void;
  children: React.ReactNode;
}) => {
  // Pixels dragged since pointerdown; null when not dragging.
  const [dragOffset, setDragOffset] = useState<number | null>(null);
  const startY = useRef(0);

  const onPointerDown = (e: React.PointerEvent) => {
    e.currentTarget.setPointerCapture(e.pointerId);
    startY.current = e.clientY;
    setDragOffset(0);
  };

  const onPointerMove = (e: React.PointerEvent) => {
    if (dragOffset !== null) {
      setDragOffset(e.clientY - startY.current);
    }
  };

  const onPointerUp = () => {
    if (dragOffset === null) {
      return;
    }
    if (Math.abs(dragOffset) < TAP_THRESHOLD_PX) {
      setExpanded(!expanded);
    } else if (expanded && dragOffset > COLLAPSE_DRAG_THRESHOLD_PX) {
      setExpanded(false);
    } else if (!expanded && dragOffset < -COLLAPSE_DRAG_THRESHOLD_PX) {
      setExpanded(true);
    }
    setDragOffset(null);
  };

  const restingY = expanded ? "0px" : `calc(100% - ${PEEK})`;
  const draggedY = `calc(${restingY} + ${dragOffset ?? 0}px)`;

  return (
    <div
      className="absolute bottom-0 left-0 z-10000 w-full"
      style={{
        // Follow the finger while dragging, but never above fully-open or
        // below the peek.
        transform: `translateY(clamp(0px, ${draggedY}, calc(100% - ${PEEK})))`,
        transition: dragOffset === null ? "transform 0.25s ease" : "none",
      }}
    >
      <div className="rounded-t-2xl border-t border-gray-200 bg-white/95 shadow-[0_-4px_16px_rgba(0,0,0,0.15)] backdrop-blur">
        <div
          className="flex cursor-grab touch-none justify-center py-4"
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
        >
          <div className="h-1 w-10 rounded-full bg-gray-300" />
        </div>
        <div className="max-h-[55vh] overflow-y-auto px-4 pb-4">{children}</div>
      </div>
    </div>
  );
};

export default BottomSheet;
